import React, { useCallback, useMemo, useState } from 'react';
import { FiCalendar, FiDownload } from 'react-icons/fi';
import { StatisticsSectionContainer, formatNumber, formatCurrency } from './StatisticsSectionLayout';

const PLAN_LABELS = {
  free: 'Free',
  founder: 'Fundador',
  pro: 'Pro',
  premium: 'Premium',
  elite: 'Elite',
};

const DATE_FORMATTER = new Intl.DateTimeFormat('es-MX', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

const DATE_TIME_FORMATTER = new Intl.DateTimeFormat('es-MX', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const toDateInputValue = date => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const buildInitialDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 29);
  return {
    start: toDateInputValue(start),
    end: toDateInputValue(end),
  };
};

const parseDateOnly = (value, { endOfDay = false } = {}) => {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(part => Number(part));
  if (!year || !month || !day) return null;
  if (endOfDay) {
    return new Date(year, month - 1, day, 23, 59, 59, 999);
  }
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

const parseIsoDate = value => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

const formatDateLabel = value => {
  const date = parseIsoDate(value) || parseDateOnly(value);
  if (!date) return '';
  return DATE_FORMATTER.format(date);
};

const formatDateTimeLabel = value => {
  const date = parseIsoDate(value);
  if (!date) return '';
  return DATE_TIME_FORMATTER.format(date);
};

const buildPeriodLabel = (start, end) => {
  if (!start && !end) return 'Periodo personalizado';
  if (start && end) {
    return `${formatDateLabel(start)} – ${formatDateLabel(end)}`;
  }
  if (start) {
    return `Desde ${formatDateLabel(start)}`;
  }
  return `Hasta ${formatDateLabel(end)}`;
};

const sanitizeVisitorEntry = (entry, canViewVisitorDetails) => {
  const base = {
    id: entry.id || '',
    created: entry.created || '',
    isGuest: Boolean(entry.isGuest),
    viewerId: entry.viewerId || '',
    viewerLabel: entry.viewerLabel || '',
    viewerEmail: entry.viewerEmail || '',
    viewerUsername: entry.viewerUsername || '',
  };

  if (canViewVisitorDetails) {
    return {
      ...base,
      displayName: base.viewerLabel || (base.isGuest ? 'Invitado' : 'Usuario registrado'),
      contact: base.viewerEmail || '—',
      reference: base.viewerUsername || (base.viewerId || base.isGuest ? '—' : ''),
    };
  }

  return {
    ...base,
    displayName: base.isGuest ? 'Invitado' : 'Usuario registrado',
    contact: 'Restringido',
    reference: base.isGuest ? 'Sesión anónima' : 'Miembro protegido',
  };
};

const createSummaryRows = ({
  publications,
  publicationLimit,
  units,
  activeRequests,
  leads,
  ppcCredits,
  companyProfileTotal,
  companyVisitorsInPeriod,
  unreadNotifications,
  planLabel,
  periodLabel,
}) => {
  const estimatedRevenue = Math.max(0, publications * 6500 + activeRequests * 11500);
  return [
    {
      id: 'plan',
      segment: 'Cuenta',
      indicator: 'Plan activo',
      value: planLabel,
      period: periodLabel,
      notes: publicationLimit
        ? `Límite de publicaciones: ${formatNumber(publicationLimit)}`
        : 'Actualiza tu plan para definir límites',
    },
    {
      id: 'publications',
      segment: 'Marketing',
      indicator: 'Publicaciones activas',
      value: formatNumber(publications),
      period: periodLabel,
      notes: publicationLimit
        ? `${formatNumber(publications)} de ${formatNumber(publicationLimit)} en uso`
        : 'Sin límite establecido',
    },
    {
      id: 'requests',
      segment: 'Comercial',
      indicator: 'Solicitudes activas',
      value: formatNumber(activeRequests),
      period: periodLabel,
      notes:
        activeRequests > 0
          ? 'Asegura seguimiento en menos de 2 horas'
          : 'Sin solicitudes pendientes en el periodo',
    },
    {
      id: 'units',
      segment: 'Operación',
      indicator: 'Unidades registradas',
      value: formatNumber(units),
      period: periodLabel,
      notes:
        units > 0
          ? 'Unidades disponibles en tu catálogo'
          : 'Registra unidades para recibir matches automáticos',
    },
    {
      id: 'leads',
      segment: 'Marketing',
      indicator: 'Leads disponibles',
      value: formatNumber(leads),
      period: periodLabel,
      notes:
        leads > 0
          ? 'Créditos listos para activar campañas'
          : 'Recarga créditos para generar nuevos leads',
    },
    {
      id: 'ppc',
      segment: 'Marketing',
      indicator: 'Créditos PPC',
      value: formatNumber(ppcCredits),
      period: periodLabel,
      notes:
        ppcCredits > 0
          ? 'Utiliza créditos para destacar publicaciones'
          : 'Sin créditos. Contrata un paquete para activarlos.',
    },
    {
      id: 'company-visits-period',
      segment: 'Audiencia',
      indicator: 'Visitas a la empresa (periodo)',
      value: formatNumber(companyVisitorsInPeriod),
      period: periodLabel,
      notes:
        companyVisitorsInPeriod > 0
          ? 'Registros dentro del rango seleccionado.'
          : 'Aún no se registran visitas en este periodo.',
    },
    {
      id: 'company-visits-total',
      segment: 'Audiencia',
      indicator: 'Visitas a la empresa (total)',
      value: formatNumber(companyProfileTotal),
      period: periodLabel,
      notes: 'Total histórico registrado en la ficha de la empresa.',
    },
    {
      id: 'sla',
      segment: 'Seguimiento',
      indicator: 'Alertas sin leer',
      value: formatNumber(unreadNotifications),
      period: periodLabel,
      notes:
        unreadNotifications > 0
          ? 'Atiende las notificaciones para mejorar tu SLA'
          : 'No tienes alertas pendientes',
    },
    {
      id: 'revenue',
      segment: 'Finanzas',
      indicator: 'Ingresos estimados',
      value: formatCurrency(estimatedRevenue),
      period: periodLabel,
      notes: 'Estimación basada en publicaciones y solicitudes activas',
    },
  ];
};

const VISITOR_TABLE_COLUMNS = [
  { key: 'date', label: 'Fecha' },
  { key: 'visitor', label: 'Visitante' },
  { key: 'type', label: 'Tipo' },
  { key: 'contact', label: 'Contacto' },
  { key: 'reference', label: 'Referencia' },
];

const TABLE_DEFINITIONS = {
  summary: {
    columns: [
      { key: 'segment', label: 'Categoría' },
      { key: 'indicator', label: 'Indicador' },
      { key: 'value', label: 'Valor' },
      { key: 'period', label: 'Periodo' },
      { key: 'notes', label: 'Notas' },
    ],
  },
  company_visits: {
    columns: VISITOR_TABLE_COLUMNS,
  },
};

const SUMMARY_CARD_LAYOUT = [
  {
    id: 'account',
    title: '🧾 Resumen de cuenta',
    items: ['plan', 'revenue', 'company-visits-total'],
  },
  {
    id: 'marketing',
    title: '📊 Marketing y audiencia',
    items: ['publications', 'leads', 'ppc'],
  },
  {
    id: 'operations',
    title: '⚙️ Operación y calidad',
    items: ['requests', 'units', 'sla'],
  },
];

const toCsvValue = value => {
  const stringValue = value == null ? '' : String(value);
  return `"${stringValue.replace(/"/g, '""')}"`;
};

const buildCsv = (columns, rows) => {
  const header = columns.map(column => toCsvValue(column.label)).join(',');
  const body = rows
    .map(row => columns.map(column => toCsvValue(row[column.key] ?? '')).join(','))
    .join('\n');
  return `${header}\n${body}`;
};

const triggerCsvDownload = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};

export default function StatisticsReportsSection({
  empresaValues = {},
  metrics = {},
  activeRequests = 0,
  unreadNotifications = 0,
  profileViews = 0,
  visitorDetails = [],
  canViewVisitorDetails = false,
}) {
  const publications = Number(metrics.publications) || 0;
  const units = Number(metrics.units) || 0;
  const leads = Number(empresaValues.leadCredits) || 0;
  const ppcCredits = Number(empresaValues.ppcCredits) || 0;
  const publicationLimit = Number(empresaValues.publicationLimit) || 0;
  const membershipSlug = String(empresaValues.membershipPlan || 'free').toLowerCase();
  const planLabel = PLAN_LABELS[membershipSlug] || 'Plan sin asignar';

  const { start: initialStart, end: initialEnd } = useMemo(buildInitialDateRange, []);
  const [startDate, setStartDate] = useState(initialStart);
  const [endDate, setEndDate] = useState(initialEnd);
  const [selectedDataset, setSelectedDataset] = useState('summary');

  const startDateObj = useMemo(() => parseDateOnly(startDate, { endOfDay: false }), [startDate]);
  const endDateObj = useMemo(() => parseDateOnly(endDate, { endOfDay: true }), [endDate]);
  const periodLabel = useMemo(() => buildPeriodLabel(startDate, endDate), [startDate, endDate]);

  const datasetOptions = useMemo(
    () => [
      {
        id: 'summary',
        label: 'Operación general',
        description: 'Consolidado para exportar tus indicadores clave y recursos disponibles.',
      },
      {
        id: 'company_visits',
        label: 'Visitas a la empresa',
        description: canViewVisitorDetails
          ? 'Detalle de visitantes a la ficha de tu empresa.'
          : 'Listado anonimizado de visitas a tu empresa.',
      },
    ],
    [canViewVisitorDetails]
  );

  const companyVisitRows = useMemo(() => {
    if (!Array.isArray(visitorDetails) || visitorDetails.length === 0) {
      return [];
    }
    return visitorDetails
      .map(entry => ({
        entry,
        sanitized: sanitizeVisitorEntry(entry, canViewVisitorDetails),
      }))
      .filter(({ entry }) => {
        const createdDate = parseIsoDate(entry.created);
        if (!createdDate) return false;
        if (startDateObj && createdDate < startDateObj) return false;
        if (endDateObj && createdDate > endDateObj) return false;
        return true;
      })
      .map(({ entry, sanitized }) => ({
        id: entry.id || entry.created,
        date: formatDateTimeLabel(entry.created),
        visitor: sanitized.displayName,
        type: sanitized.isGuest ? 'Invitado' : 'Miembro registrado',
        contact: sanitized.contact,
        reference: sanitized.reference || '—',
        rawCreated: entry.created,
      }));
  }, [visitorDetails, canViewVisitorDetails, startDateObj, endDateObj]);

  const summaryRows = useMemo(
    () =>
      createSummaryRows({
        publications,
        publicationLimit,
        units,
        activeRequests,
        leads,
        ppcCredits,
        companyProfileTotal: profileViews,
        companyVisitorsInPeriod: companyVisitRows.length,
        unreadNotifications,
        planLabel,
        periodLabel,
      }),
    [
      activeRequests,
      companyVisitRows,
      leads,
      planLabel,
      periodLabel,
      publicationLimit,
      publications,
      ppcCredits,
      profileViews,
      unreadNotifications,
      units,
    ]
  );

  const summaryRowsById = useMemo(() => {
    const map = {};
    summaryRows.forEach(row => {
      if (row.id) {
        map[row.id] = row;
      }
    });
    return map;
  }, [summaryRows]);

  const summaryCardData = useMemo(
    () =>
      SUMMARY_CARD_LAYOUT.map(card => ({
        ...card,
        rows: card.items.map(itemId => summaryRowsById[itemId]).filter(Boolean),
      })).filter(card => card.rows.length),
    [summaryRowsById]
  );

  const selectedDatasetCountLabel = useMemo(() => {
    switch (selectedDataset) {
      case 'company_visits':
        return `${formatNumber(companyVisitRows.length)} visitas a la empresa en el periodo`;
      default:
        return 'Consolidado operativo listo para exportar';
    }
  }, [selectedDataset, companyVisitRows.length]);

  const currentTable = useMemo(() => {
    const definition = TABLE_DEFINITIONS[selectedDataset] || TABLE_DEFINITIONS.summary;
    const rows = selectedDataset === 'company_visits' ? companyVisitRows : summaryRows;
    return {
      columns: definition.columns,
      rows,
    };
  }, [companyVisitRows, selectedDataset, summaryRows]);

  const handleDownload = useCallback(() => {
    if (!currentTable.rows.length) {
      return;
    }
    const csvContent = buildCsv(currentTable.columns, currentTable.rows);
    const filename = `flotte-${selectedDataset}-${toDateInputValue(new Date())}.csv`;
    triggerCsvDownload(csvContent, filename);
  }, [currentTable.columns, currentTable.rows, selectedDataset]);

  const currentDatasetMeta = datasetOptions.find(option => option.id === selectedDataset);
  const formattedStartLabel = useMemo(() => formatDateLabel(startDate) || 'Inicio', [startDate]);
  const formattedEndLabel = useMemo(() => formatDateLabel(endDate) || 'Fin', [endDate]);
  const isDownloadDisabled = currentTable.rows.length === 0;
  const executiveStats = useMemo(
    () => [
      {
        id: 'visits-total',
        label: 'Total visitas',
        value: formatNumber(profileViews),
        helper: `${formatNumber(companyVisitRows.length)} en el periodo`,
      },
      {
        id: 'leads-total',
        label: 'Total leads',
        value: formatNumber(leads),
        helper: 'Créditos disponibles',
      },
      {
        id: 'activity',
        label: 'Actividad',
        value: formatNumber(activeRequests),
        helper: 'Solicitudes activas',
      },
    ],
    [profileViews, companyVisitRows.length, leads, activeRequests]
  );

  return (
    <div className="statistics-page statistics-page--executive">
      <StatisticsSectionContainer
        title="Reportes ejecutivos"
        subtitle="Genera y descarga tu historial operativo."
        periodLabel=""
      >
        <div className="reports-toolbar">
          <div className="reports-tabs" data-active={selectedDataset}>
            {datasetOptions.map(option => (
              <button
                key={option.id}
                type="button"
                className={`reports-tabs__option${option.id === selectedDataset ? ' is-active' : ''}`}
                onClick={() => setSelectedDataset(option.id)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <div className="reports-toolbar__date">
            <div className="report-date-button" data-empty={!startDate || !endDate ? 'true' : undefined}>
              <span className="report-date-button__icon" aria-hidden="true">
                <FiCalendar />
              </span>
              <div className="report-date-button__labels" aria-live="polite">
                <span>{formattedStartLabel}</span>
                <span className="report-date-button__dash">–</span>
                <span>{formattedEndLabel}</span>
              </div>
              <label className="statistics-sr-only" htmlFor="statistics-start-date">
                Desde
              </label>
              <input
                id="statistics-start-date"
                type="date"
                value={startDate}
                max={endDate || undefined}
                onChange={event => setStartDate(event.target.value)}
                className="report-date-button__input report-date-button__input--start"
              />
              <label className="statistics-sr-only" htmlFor="statistics-end-date">
                Hasta
              </label>
              <input
                id="statistics-end-date"
                type="date"
                value={endDate}
                min={startDate || undefined}
                onChange={event => setEndDate(event.target.value)}
                className="report-date-button__input report-date-button__input--end"
              />
            </div>
          </div>
          <button
            type="button"
            className="report-export-btn"
            onClick={handleDownload}
            disabled={isDownloadDisabled}
          >
            <FiDownload aria-hidden="true" />
            <span>Descargar CSV</span>
          </button>
        </div>

        <div className="reports-mini-stats">
          {executiveStats.map(stat => (
            <article key={stat.id} className="reports-mini-stats__card">
              <span>{stat.label}</span>
              <strong>{stat.value}</strong>
              <small>{stat.helper}</small>
            </article>
          ))}
        </div>

        {selectedDataset === 'summary' ? (
          summaryCardData.length ? (
            <div className="report-card-stack">
              {summaryCardData.map(card => (
                <section key={card.id} className="report-card">
                  <header className="report-card__header">
                    <h3>{card.title}</h3>
                  </header>
                  <div className="report-card-grid">
                    {card.rows.map(row => (
                      <article key={row.id || row.indicator} className="report-card-item">
                        <span className="report-card-item__label">{row.indicator}</span>
                        <span className="report-card-item__value">{row.value}</span>
                        {row.notes ? <p className="report-card-item__note">{row.notes}</p> : null}
                      </article>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          ) : (
            <div className="statistics-empty-state statistics-empty-state--document">
              <strong>No hay datos para mostrar.</strong>
              <span>Registra actividad para generar un resumen en este periodo.</span>
            </div>
          )
        ) : (
          <article className="statistics-table-card statistics-table-card--document">
            <div className="statistics-document-sheet">
              <header className="statistics-document-sheet__header">
                <div className="statistics-document-logo" aria-hidden="true">
                  <span>F</span>
                </div>
                <div className="statistics-document-sheet__titles">
                  <p>Flotte Analytics</p>
                  <h2>Reporte mensual</h2>
                  <span>{periodLabel}</span>
                </div>
                {currentDatasetMeta ? (
                  <div className="statistics-document-sheet__dataset">
                    <strong>{currentDatasetMeta.label}</strong>
                    {currentDatasetMeta.description ? <small>{currentDatasetMeta.description}</small> : null}
                  </div>
                ) : null}
              </header>

              <div className="statistics-table-meta">
                <span>
                  {currentTable.rows.length
                    ? `${formatNumber(currentTable.rows.length)} registros listos para exportar`
                    : 'Sin registros para el periodo seleccionado'}
                </span>
                <span>{selectedDatasetCountLabel}</span>
              </div>

              <div className="statistics-table-wrapper">
                {currentTable.rows.length ? (
                  <table className="statistics-data-table">
                    <thead>
                      <tr>
                        {currentTable.columns.map(column => (
                          <th key={column.key}>{column.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {currentTable.rows.map(row => (
                        <tr key={row.id || row[currentTable.columns[0].key]}>
                          {currentTable.columns.map(column => (
                            <td
                              key={column.key}
                              className={
                                column.key === 'value'
                                  ? 'statistics-data-table__value'
                                  : column.key === 'notes'
                                  ? 'statistics-data-table__notes'
                                  : undefined
                              }
                            >
                              {row[column.key] ?? '—'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="statistics-empty-state statistics-empty-state--document">
                    <strong>No hay datos para mostrar.</strong>
                    <span>
                      Ajusta el periodo o selecciona otro dataset para preparar la exportación.
                    </span>
                  </div>
                )}
              </div>
            </div>
          </article>
        )}
      </StatisticsSectionContainer>
    </div>
  );
}
